<template>
  <div
    id="product-form-drawer"
    class="product-form-drawer"
    :class="{ [$class('loading')]: loading }"
  >
    <div
      v-if="submission.hasSubmitted"
      class="product-form-drawer__confirmation"
    >
      <div class="product-form-drawer__confirmation-product">
        <responsive-image
          class="product-carousel__image"
          :image="filteredMediaImage || product.featuredImage"
          ratio="4:5"
        />

        <div class="product-form-drawer__confirmation-product-details">
          <div>
            <p
              class="text-other-label-bold"
              v-text="product.title"
            />

            <product-prices
              class="product-form-drawer__confirmation-prices-container"
              :available="variant.available"
              :render-final-sale-copy="isFinalSale"
              :compare-at-price="variant.compareAtPrice?.amount"
              :loading="state === 'loading'"
              :price="variant.price?.amount"
              :varies="variant.price?.varies"
            />
          </div>

          <div class="product-form-drawer__confirmation-variant-details">
            <div class="product-form-drawer__confirmation-swatch-container">
              <span
                class="product-form-drawer__confirmation-swatch"
                :style="getSwatch()"
              />

              <p v-text="getConfirmationColor" />
            </div>

            <div class="product-form-drawer__confirmation-details-row">
              <p v-text="getConfirmationSize" />
              <p v-text="getConfirmationQuantity" />
            </div>
          </div>
        </div>
      </div>

      <p
        :style="{ color : getColor(section.altermessage.message)}"
        v-text="parsedAlterMessage(section.altermessage.message)"
      />

      <div class="product-form-drawer__confirmation-buttons">
        <btn
          :label="getOpenCart"
          modifiers="center,primary"
          type="button"
          @click.prevent="openCartDrawer"
        />

        <btn
          :label="$string('product.form.continue_shopping')"
          modifiers="center,secondary"
          type="button"
          @click.prevent="closeProductFormDrawer"
        />
      </div>
    </div>

    <form
      v-else
      class="product-form-drawer__form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        class="product-form-drawer__options"
        :drawer-color-swatch="activeSwatch"
        :filtered-media-image="filteredMediaImage"
        is-drawer
        is-product-form-drawer
        :is-size-guide-drawer="isSizeGuideExpanded"
        :loading="loading"
        :namespace="namespace"
        :out-of-stock-expanded="outOfStockExpanded"
        :product="product"
        :section="section"
        :variant="variant"
        @add-to-cart="handleAddToCart"
        @open-size-guide="handleOpenSizeGuide"
        @open-notify-me-drawer="handleOpenNotifyMeDrawer"
        @update-variant="handleUpdateVariant"
      />

      <input
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <recipient-form
        v-if="product.giftCard"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />

      <btn
        v-if="showAddToCartButton"
        class="product-form__add-to-cart"
        :label="addToCartText"
        modifiers="add-to-cart, center"
        type="submit"
      >
        <template #hoverIcon>
          <icon-circle />
        </template>
      </btn>
    </form>
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Object} activeSwatch - Current color swatch.
 * @param {Object} filteredMediaImage - First image in media array.
 * @param {Boolean} isFinalSale - If product is final sale.
 * @param {Boolean} isSizeGuideExpanded - If size guide drawer is expanded.
 * @param {Boolean} [isSticky] - If instantiation is sticky.
 * @param {String|Boolean} preorderDate - Preorder date.
 * @param {Boolean} [loading] - Loading state.
 * @param {String} namespace - Form namespace.
 * @param {Boolean} outOfStockExpanded - If the notify me drawer is open.
 * @param {Object} overlay - Overlay API.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits openNotifyMeDrawer - Emit event to open notify me drawer.
 * @emits openSizeGuide - Emit event to open size guide drawer.
 * @emits updateConfirmationTitle - Emit new product form drawer title.
 * @emits updateExpanded - Emit if drawer was closed.
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import ProductOptions from '~async/product-form/product-options'
import RecipientForm from '~async/product-form/recipient-form'
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import ProductPrices from '~global/product-prices/product-prices'

import IconCircle from '~icons/scribbles/circle.svg'

import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductFormDrawer',

  components: {
    Btn,
    IconCircle,
    ProductOptions,
    ProductPrices,
    RecipientForm,
    ResponsiveImage,
  },

  props: {
    activeSwatch: {
      type: Object,
      default: () => ({
        code: '',
        label: '',
        value: '',
      }),
    },
    filteredMediaImage: {
      type: Object,
      default: () => ({}),
    },
    isFinalSale: {
      type: Boolean,
      default: false,
    },
    isSizeGuideExpanded: {
      type: Boolean,
      default: false,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    preorderDate: {
      type: [Boolean, String],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    outOfStockExpanded: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayRecipientForm: true,
        hideGiftCardOptionNames: true,
        id: '',
        resetRecipientFormOnAdd: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'open-notify-me-drawer': (payload) => typeof payload === 'boolean',
    'open-size-guide': (payload) => typeof payload === 'boolean',
    'update-confirmation-title': (payload) => typeof payload === 'boolean',
    'update-expanded': (payload) => typeof payload === 'boolean',
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      activeCode: '',
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      quantity: 1,
      submission: {
        hasAttemptedSubmission: false,
        hasSubmitted: false,
        useAddMoreText: false,
      },
      state: 'ready',
      timeout: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant && this.submission.hasAttemptedSubmission) {
        return this.$string('product.cta.select_size')
      }

      if (this.variant && !this.variant.available) {
        return this.$string('product.cta.sold_out')
      }

      if (this.preorderDate) {
        return this.$string('product.cta.preorder')
      }

      return this.submission.useAddMoreText
        ? this.$string('product.cta.add_more_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Return confirmation colour string.
     * @returns {String}
     */
    getConfirmationColor() {
      return this.$string('product.form.confirmation.color')
        .replace('{{ color }}', this.activeSwatch.label)
    },

    /**
     * Return confirmation quantity string.
     * @returns {String}
     */
    getConfirmationQuantity() {
      return this.$string('product.form.confirmation.quantity')
        .replace('{{ quantity }}', this.quantity)
    },

    /**
     * Return confirmation size.
     * @returns {String}
     */
    getConfirmationSize() {
      const size = this.variant.selectedOptions.find((option) => {
        return option.name === 'Size'
      })

      return this.$string('product.form.confirmation.size')
        .replace('{{ size }}', size.value)
    },

    /**
     * Return open cart string.
     * @returns {String}
     */
    getOpenCart() {
      return this.$string('product.form.open_cart')
        .replace('{{ cart_count }}', this.cart.item_count)
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.product.giftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null
      }

      const filteredFields = {}

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return
        }

        filteredFields[key] = value
      })

      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      }
    },

    /**
     * Compute whether to render the add to cart button.
     * @returns {Boolean}
     */
    showAddToCartButton() {
      if (this.isMobile) {
        return this.isSticky
      }

      return true
    },

    /**
     * Compute whether to show product options.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.isSticky && this.isMobile) {
        return false
      }

      return !this.product.hasOnlyDefaultVariant
    },
  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString()
  },

  mounted() {
    this.setInitialActiveCode()
    this.overlay.addCallback(this.overlayCallback)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle the text of alter message
     */
    parsedAlterMessage(item) {
      const fixedJsonString = item.replace(/}\s*{/g, '},{')
      const objArray = JSON.parse(fixedJsonString)
      const message = objArray[0].message.children[0].children[0].value
      return message
    },

    /**
     *  Handle the color for alter message
     */
    getColor(item) {
      const message = JSON.parse(item)
      return message[0].color
    },

    /**
     * Set callback for when the overlay closes.
     */
    overlayCallback() {
      this.$emit('update-expanded', false)

      if (!this.submission.hasSubmitted) {
        return
      }

      this.resetState()
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.submission.hasAttemptedSubmission = true
        this.error = this.$string('product.form.select_variant')
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        this.storybookAddToCart()
        return
      }

      const items = this.buildCartItem()

      try {
        await this.addItem(items)
        this.setProductAddedToCart()
      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Built cart item array.
     * @returns {Array}
     */
    buildCartItem() {
      return [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
              ...this.giftCardLineItemProperties,
              ...this.lineItemProperties,
            }
            : this.lineItemProperties,
          quantity: this.quantity,
        },
      ]
    },

    /**
     * Set appropriate variables when a product has successfully been added to
     * the cart.
     */
    setProductAddedToCart() {
      this.state = 'added'
      this.submission.hasSubmitted = true
      this.submission.useAddMoreText = true
      this.$emit('update-confirmation-title', true)
    },

    /**
     * Manually open the cart drawer.
     */
    openCartDrawer() {
      if (!this.getComponentExists('cart-drawer')) {
        return
      }

      this.closeProductFormDrawer()

      if (this.isMobile) {
        this.openOverlay({
          component: 'cart-drawer-mobile',
          ignoreDismissed: true,
          namespace: 'cartDrawerMobile',
        })

        return
      }

      this.openOverlay({
        component: 'cart-drawer-mobile',
        ignoreDismissed: true,
        namespace: 'cartDrawerMobile',
      })
    },

    /**
     * Handle emit event to open the notify me drawer.
     */
    handleOpenNotifyMeDrawer() {
      this.$emit('open-notify-me-drawer', true)
    },

    /**
     * Close product form drawer.
     */
    closeProductFormDrawer() {
      if (this.submission.hasSubmitted) {
        this.resetState()
      }

      this.closeOverlay({
        component: 'product-form-drawer',
        namespace: 'productFormDrawer',
      })
    },

    /**
     * Return background image for swatch in product confirmation popup.
     * @returns {String}
     */
    getSwatch() {
      return `background-image: url('${this.variant.swatchUrl}')`
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard && this.section.resetRecipientFormOnAdd) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        }
      }

      this.submission.hasSubmitted = false
      this.$emit('update-confirmation-title', false)
      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm
    },

    /**
     * Emit event to open the size guide drawer.
     * @param {Boolean} payload - payload from product options.
     */
    handleOpenSizeGuide(payload) {
      this.$emit('open-size-guide', payload)
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof error === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },

    /**
     * Set the initial active code based on the selected colour.
     */
    setInitialActiveCode() {
      this.activeCode = this.activeSwatch.code
    },

    /**
     * Helper function for storybook add to cart event.
     */
    storybookAddToCart() {
      if (!this.variant.available) {
        this.error = this.$string('product.form.sold_out')
        this.state = 'ready'
        return
      }

      this.timeout = setTimeout(() => {
        this.state = 'added'
      }, this.$timing('slow'))

      this.timeout = setTimeout(() => {
        this.state = 'ready'
      }, this.$timing('message'))
    },
  },
}
</script>

<style lang="scss">
@import './product-form-drawer';
</style>
